import React, { ReactElement, ReactNode } from 'react';

import PropTypes from 'prop-types';

import { parseDate, DateTimeOptions } from '@adsk/alloy-react-datetime';
import { useTheme } from '@adsk/alloy-react-styles';
import { stylePropType, StylableComponent } from '@adsk/alloy-react-theme';

import { UserWithNameAsNode, userWithNameAsNodeShape } from './consts';
import Footer, { FooterProps } from './layout/Footer';
import Header, { HeaderProps } from './layout/Header';

const defaultRenderHeader = (props: HeaderProps) => <Header {...props} />;
const defaultRenderFooter = (props: FooterProps) => <Footer {...props} />;

type DateType = Date | string | number;

type ActivityCardProps = Omit<StylableComponent<HTMLDivElement>, 'title'> & {
  title?: ReactNode;
  user?: UserWithNameAsNode;
  date?: DateType;
  footer?: ReactNode;
  renderHeader?: (props: HeaderProps) => ReactElement | null;
  renderFooter?: (props: FooterProps) => ReactElement | null;
  isFirst?: boolean;
  isLast?: boolean;
  dateOptions?: DateTimeOptions;
};

const normalizeDate = (date: DateType | undefined): Date | undefined => {
  if (!date) {
    return undefined;
  }
  return date instanceof Date ? date : parseDate(date);
};

/**
 * ActivityCard component. Describes a single activity update event.
 */
const ActivityCard = React.forwardRef<HTMLDivElement, ActivityCardProps>(
  (
    {
      className,
      style,
      title,
      user,
      date,
      dateOptions,
      isFirst = true,
      isLast = true,
      renderHeader = defaultRenderHeader,
      renderFooter = defaultRenderFooter,
      children,
      ...props
    },
    ref,
  ) => {
    const activityLogTokens = useTheme().tokens?.component?.activityLog;
    const timelineTokens = activityLogTokens?.timeline;
    const contentTokens = activityLogTokens?.content;

    return (
      <div
        ref={ref}
        className={className}
        css={[
          {
            display: 'flex',
            backgroundColor: activityLogTokens?.backgroundColor,
            width: '100%',
          },
          style,
        ]}
        {...props}
      >
        <div
          css={[
            {
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              paddingLeft: timelineTokens?.paddingLeft,
            },
          ]}
        >
          {
            <div
              css={[
                {
                  height: 23,
                  width: 1,
                  backgroundColor: isFirst
                    ? 'transparent'
                    : timelineTokens?.['line-color'],
                  marginRight: 1,
                },
              ]}
            />
          }
          {
            <div
              css={[
                {
                  borderRadius: timelineTokens?.['dot-border-radius'],
                  borderWidth: timelineTokens?.['dot-border-width'],
                  borderStyle: 'solid',
                  borderColor: timelineTokens?.['dot-border-color'],
                  width: 8,
                  height: 8,
                },
              ]}
            />
          }
          {!isLast && (
            <div
              css={[
                {
                  flex: 1,
                  width: 1,
                  backgroundColor: timelineTokens?.['line-color'],
                  marginRight: 1,
                },
              ]}
            />
          )}
        </div>
        <div
          css={[
            {
              display: 'flex',
              flexDirection: 'column',
              paddingTop: contentTokens?.paddingTop,
              paddingRight: contentTokens?.paddingRight,
              paddingBottom: contentTokens?.paddingBottom,
              paddingLeft: contentTokens?.paddingLeft,
              overflow: 'auto',
              flex: 1,
            },
          ]}
        >
          {renderHeader({ children: title })}
          {children}
          {renderFooter({
            user,
            date: normalizeDate(date),
            dateOptions,
            style: {
              marginTop: children
                ? contentTokens?.['footer-margin-top']
                : undefined,
            },
          })}
        </div>
      </div>
    );
  },
);

ActivityCard.displayName = 'ActivityCard';

ActivityCard.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** The card title */
  title: PropTypes.any,
  /** The user to show the activity for */
  user: userWithNameAsNodeShape,
  /** The date to show the activity for (Note: if using a number Unix Epoch Time is expected in milliseconds) */
  date: PropTypes.oneOfType([
    PropTypes.instanceOf(Date),
    PropTypes.string,
    PropTypes.number,
  ]),
  /** Whether this card is the first in activities list */
  isFirst: PropTypes.bool,
  /** Whether this card is the last in activities list */
  isLast: PropTypes.bool,
  /** Render custom header content. Props: ({ title }) */
  renderHeader: PropTypes.func,
  /** Render custom footer content */
  renderFooter: PropTypes.func,
  /** The card content */
  children: PropTypes.any,
};

export default Object.assign(ActivityCard, {
  Header,
  Footer,
});
